import { RefObject, useCallback, useState } from "react";
import { Company } from "../../../setups/types/setupTypes";
import { Employee } from "../../types/processTypes";
import {
  buildAndSavePdfFromDom,
  buildBulkCvFileName,
} from "../utils/buildCvPdf";

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Downloads one PDF per office by capturing each already-mounted CV in the
 * on-screen preview (same toCanvas path as PremiumTemplate / single download).
 */
export function useBulkOfficePdfDownload(
  previewRootRef: RefObject<HTMLElement | null>,
) {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadBulk = useCallback(
    async (offices: Company[], employee: Employee) => {
      if (!employee.givenName || offices.length === 0) return;

      const previewRoot = previewRootRef.current;
      if (!previewRoot) return;

      setIsDownloading(true);
      try {
        for (const office of offices) {
          const officeId = office._id ? String(office._id) : "";
          if (!officeId) continue;

          const scope = previewRoot.querySelector(
            `[data-cv-office="${officeId}"]`,
          ) as HTMLElement | null;
          if (!scope) continue;

          scope.scrollIntoView({ block: "nearest" });
          await waitForPaint();

          const numberOfPages = office.cvTemplate?.numberOfPages ?? 1;
          await buildAndSavePdfFromDom(
            buildBulkCvFileName(employee, office),
            numberOfPages,
            scope,
          );
        }
      } finally {
        setIsDownloading(false);
      }
    },
    [previewRootRef],
  );

  return { downloadBulk, isDownloading };
}
